import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setModuleSensors } from "../../../store/features/moduleSlice";
import { SENSORS } from "../../../constants/sensors";
import { SensorType } from "../../../types/sensors";
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import {
  calculateSensorsTons,
} from "../../../utils/calculations";
import { calculateSensorCost } from "../../../constants/sensors";

/**
 * Module Sensors Step Component
 * Handles sensor system selection and configuration for modules
 */
export const ModuleSensorsStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const sensors = useAppSelector((state) => state.module.moduleConfig?.systems.sensors);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Convert SENSOR_TYPES Record to array for filtering
  const sensorTypesArray = Object.values(SENSORS).filter(
    (sensor) => sensor.techLevel <= moduleConfig.techLevel
  );

  const handleSensorChange = (type: string) => {
    if (type === "NONE") {
      // Clear sensors
      dispatch(setModuleSensors(null));
      return;
    }

    const sensorConfig = SENSORS[type as SensorType];

    const sensor = {
      type: type as SensorType,
      name: sensorConfig.name || "Unknown Sensors",
      description: sensorConfig.description || "No description available",
      capabilities: sensorConfig.capabilities ? [...sensorConfig.capabilities] : [],
      dm: sensorConfig.dm || 0,
      power: sensorConfig.power || 0,
      tons: calculateSensorsTons(type as SensorType) || 0,
      cost: calculateSensorCost(type as SensorType, 100), // Use 100 as default ship size for modules
      techLevel: sensorConfig.techLevel || 8,
      rating: 1, // Default rating for basic sensors
    };

    dispatch(setModuleSensors(sensor));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Module Sensor Systems Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Select sensor suites for your module
        </p>
      </div>

      {/* Module Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Module Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.size)} tons</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Tech Level:</span>
            <div className="text-blue-900 dark:text-blue-100">TL {moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Available Power:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {moduleConfig.calculations.totalPower} Power
            </div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Remaining Tons:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.calculations.remainingTons)} tons</div>
          </div>
        </div>
      </div>

      {/* Sensor Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Sensor Configuration
        </h3>
        
        <div className="flex flex-col space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Type</label>
          <select
            value={sensors ? sensors.type : "NONE"}
            onChange={(event) =>
              handleSensorChange(event.target.value)
            }
            className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="NONE">None Installed</option>
            {sensorTypesArray.map((spec) => (
              <option
                key={spec.type}
                value={spec.type}
                disabled={spec.techLevel > moduleConfig.techLevel}
                title={
                  spec.techLevel > moduleConfig.techLevel
                    ? `Requires TL ${spec.techLevel}`
                    : `Min TL ${spec.techLevel}`
                }
              >
                {spec.name} (Min TL {spec.techLevel})
              </option>
            ))}
          </select>
        </div>

        {sensors && (
          <div className="space-y-4 mt-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Selected Sensors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p><span className="font-medium text-gray-900 dark:text-gray-100">Type:</span> <span className="text-gray-700 dark:text-gray-300">{sensors.name}</span></p>
                  <p>
                    <span className="font-medium text-gray-900 dark:text-gray-100">DM:</span> <span className="text-gray-700 dark:text-gray-300">{sensors.dm > 0 ? "+" : ""}
                    {sensors.dm}</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p><span className="font-medium text-gray-900 dark:text-gray-100">Power:</span> <span className="text-gray-700 dark:text-gray-300">{formatNumber(sensors.power)} Power</span></p>
                  <p><span className="font-medium text-gray-900 dark:text-gray-100">Weight:</span> <span className="text-gray-700 dark:text-gray-300">{formatNumber(sensors.tons)} tons</span></p>
                  <p><span className="font-medium text-gray-900 dark:text-gray-100">Cost:</span> <span className="text-gray-700 dark:text-gray-300">{formatCurrency(sensors.cost)}</span></p>
                  <p><span className="font-medium text-gray-900 dark:text-gray-100">Tech Level:</span> <span className="text-gray-700 dark:text-gray-300">{sensors.techLevel}</span></p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>





      {/* Sticky Footer with Sensor Stats */}
      {sensors && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
            <div>
              <span className="text-gray-400 block text-sm">Power</span>
              <span className="text-xl font-bold text-yellow-400">
                {formatNumber(sensors.power)} Power
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Weight</span>
              <span className="text-xl font-bold text-blue-400">
                {formatNumber(sensors.tons)} tons
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Cost</span>
              <span className="text-xl font-bold text-green-400">
                {formatCurrency(sensors.cost)}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">DM</span>
              <span className="text-xl font-bold text-purple-400">
                {sensors.dm > 0 ? "+" : ""}
                {sensors.dm}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
